/**
 * Overgrowth hook pieces — plants that hang from a host's roof-edge hook point.
 *
 *   `hush.ivy_run.v1`        44×96, hang (22, 3): small three-lobed ivy leaves on a wandering stem.
 *   `hush.hanging_vines.v1`  36×64, hang (18, 2): a moss tuft with 3–5 thin vine strands, sparse leaves.
 *   `hush.moss_clump.v1`     64×24, hang (32, 4): a lumpy draped moss mat with a few short drips.
 *
 * One entry point, `buildOvergrowth(kind, recipe, roles, scope)`, plus thin per-kind wrappers. Every kind takes
 * `density` (0.4..1, leaf/strand/drip count) and `length` (0.6..1, scales the downward extent only — the top and the
 * hang point never move). The `hang` anchor is on paint at the top center. No definitions, no gameplay geometry.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export type OvergrowthKind = 'ivy_run' | 'hanging_vines' | 'moss_clump';
export interface OvergrowthParams {
  readonly density: number; // 0.4..1
  readonly length: number;  // 0.6..1
}
export type OvergrowthGeneratorId<K extends OvergrowthKind = OvergrowthKind> = `hush.${K}.v1`;
export type OvergrowthRecipe<K extends OvergrowthKind = OvergrowthKind> = PieceRecipe<OvergrowthGeneratorId<K>, OvergrowthParams>;

export const OVERGROWTH_FRAMES: Readonly<Record<OvergrowthKind, { w: number; h: number; hang: Point }>> = {
  ivy_run: { w: 44, h: 96, hang: { x: 22, y: 3 } },
  hanging_vines: { w: 36, h: 64, hang: { x: 18, y: 2 } },
  moss_clump: { w: 64, h: 24, hang: { x: 32, y: 4 } },
};

const recipeFor = <K extends OvergrowthKind>(kind: K, density: number, length: number): OvergrowthRecipe<K> => ({
  schema: 'hush.asset-recipe.v1', id: `hush.${kind}.default.v1`, generatorId: `hush.${kind}.v1` as OvergrowthGeneratorId<K>,
  seed: 20067018, paletteId: 'coastal_salt', params: { density, length },
});
export const HUSH_IVY_RUN = recipeFor('ivy_run', 0.8, 0.9);
export const HUSH_HANGING_VINES = recipeFor('hanging_vines', 0.8, 0.9);
export const HUSH_MOSS_CLUMP = recipeFor('moss_clump', 0.8, 0.9);

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

function num(p: unknown, k: string, lo: number, hi: number, gen: string): number {
  const v = (p as Record<string, unknown>)[k];
  if (typeof v !== 'number' || !(v >= lo && v <= hi)) throw new Error(`${gen}: ${k} must be ${lo}..${hi}`);
  return v;
}

type Rnd = (channel: string) => number;
interface Ctx { roles: ResolvedPalette; rnd: Rnd; density: number; length: number; w: number; h: number; parts: string[] }

/** Smooth closed outline through points (midpoint quadratics). */
function smooth(pts: readonly Point[]): string {
  const mid = (a: Point, b: Point) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const m0 = mid(pts[pts.length - 1], pts[0]);
  let d = `M${n(m0.x)} ${n(m0.y)}`;
  pts.forEach((p, i) => { const m = mid(p, pts[(i + 1) % pts.length]); d += `Q${n(p.x)} ${n(p.y)} ${n(m.x)} ${n(m.y)}`; });
  return d + 'Z';
}

/** Three-lobed ivy leaf, petiole at (x, y), pointing along `angle` (radians, 0 = down). */
function ivyLeaf(x: number, y: number, s: number, angle: number, fill: string, c: Ctx): string {
  const shape = [[0, 0.12], [-0.5, 0.02], [-0.66, 0.42], [-0.28, 0.6], [0, 1.02], [0.28, 0.6], [0.66, 0.42], [0.5, 0.02]];
  const ca = Math.cos(angle), sa = Math.sin(angle);
  const pts = shape.map(([u, v]) => ({ x: x + (u * ca + v * sa) * s, y: y + (-u * sa + v * ca) * s }));
  const tip = pts[4];
  return el('path', { d: smooth(pts), fill, stroke: c.roles.outline, 'stroke-width': 0.7, 'stroke-linejoin': 'round' })
    + el('path', { d: `M${n(x + sa * s * 0.15)} ${n(y + ca * s * 0.15)}L${n(x + (tip.x - x) * 0.7)} ${n(y + (tip.y - y) * 0.7)}`,
      stroke: c.roles.reedLight, 'stroke-width': 0.5, opacity: 0.8 });
}

function buildIvy(c: Ctx): string {
  const { rnd, roles, w } = c;
  const bottom = 6 + (c.h - 12) * c.length;
  const stem: Point[] = [{ x: 22, y: 2.6 }];
  for (let y = 10; y < bottom; y += 9) stem.push({ x: Math.min(w - 9, Math.max(9, 22 + (rnd(`stem.${y}`) - 0.5) * 12)), y });
  stem.push({ x: stem[stem.length - 1].x + 2, y: bottom });
  let d = `M${n(stem[0].x)} ${n(stem[0].y)}`;
  for (let i = 1; i < stem.length; i++) {
    const a = stem[i - 1], b = stem[i];
    d += `Q${n(a.x + (b.x - a.x) * 0.1 + 2)} ${n((a.y + b.y) / 2)} ${n(b.x)} ${n(b.y)}`;
  }
  const stems = el('path', { d, fill: 'none', stroke: roles.woodShadow, 'stroke-width': 1.3, 'stroke-linecap': 'round' });
  const greens = [roles.moss, roles.reedBase, roles.reedShadow];
  let leaves = '';
  const step = 7.5 / c.density;
  let i = 0;
  for (let y = 7; y < bottom - 2; y += step, i++) {
    const seg = stem.findIndex(p => p.y >= y);
    const p = stem[Math.max(1, seg)], q = stem[Math.max(0, seg - 1)];
    const t = (y - q.y) / Math.max(1, p.y - q.y);
    const x = q.x + (p.x - q.x) * t;
    const side = i % 2 ? 1 : -1;
    const s = (8.5 - 3 * (y / c.h)) * (0.85 + rnd(`leaf.${i}.s`) * 0.3);
    const ang = side * (0.5 + rnd(`leaf.${i}.a`) * 0.5);
    leaves += ivyLeaf(Math.min(w - 2 - s * 0.7, Math.max(2 + s * 0.7, x + side * 1.2)), y, s, ang, greens[Math.floor(rnd(`leaf.${i}.c`) * 3)], c);
  }
  // Attachment knot: a small cluster of leaves over a dark stem knot at the hang point.
  const knot = el('path', { d: 'M18.5 1.4H25.5L24.4 5.6H19.6Z', fill: roles.woodShadow, stroke: roles.outline, 'stroke-width': 0.7 });
  const cap = ivyLeaf(17.5, 3.6, 6.2, -0.9, roles.moss, c) + ivyLeaf(26.5, 3.6, 6.2, 0.9, roles.reedBase, c);
  return partGroup(c.parts, 'ivy-stem', stems) + partGroup(c.parts, 'ivy-leaves', leaves) + partGroup(c.parts, 'ivy-knot', cap + knot);
}

function buildVines(c: Ctx): string {
  const { rnd, roles, w } = c;
  const strands = 3 + Math.round(2 * c.density);
  let lines = '', leaves = '';
  for (let i = 0; i < strands; i++) {
    const x0 = 7 + (i + 0.5) * ((w - 14) / strands) + (rnd(`v.${i}.x`) - 0.5) * 3;
    const len = (0.5 + rnd(`v.${i}.len`) * 0.5) * (c.h - 8) * c.length;
    const sway = (rnd(`v.${i}.sway`) - 0.5) * 8;
    const x1 = Math.min(w - 3, Math.max(3, x0 + sway)), y1 = 4 + len;
    const d = `M${n(x0)} 4C${n(x0 - sway * 0.6)} ${n(4 + len * 0.35)} ${n(x1 + sway * 0.5)} ${n(4 + len * 0.65)} ${n(x1)} ${n(y1)}`;
    lines += el('path', { d, fill: 'none', stroke: roles.outline, 'stroke-width': 1.7, 'stroke-linecap': 'round' })
      + el('path', { d, fill: 'none', stroke: roles.reedShadow, 'stroke-width': 0.8, 'stroke-linecap': 'round' });
    const nLeaves = Math.max(2, Math.round((len / 10) * c.density));
    for (let j = 0; j < nLeaves; j++) {
      const t = (j + 0.6) / nLeaves;
      const bt = (u: number, a: number, b: number, cc: number, dd: number) => (1 - u) ** 3 * a + 3 * (1 - u) ** 2 * u * b + 3 * (1 - u) * u * u * cc + u ** 3 * dd;
      const px = bt(t, x0, x0 - sway * 0.6, x1 + sway * 0.5, x1), py = bt(t, 4, 4 + len * 0.35, 4 + len * 0.65, y1);
      const side = (i + j) % 2 ? 1 : -1;
      const rot = side * (35 + rnd(`v.${i}.${j}.r`) * 25);
      const fill = j % 3 === 2 ? roles.reedLight : (i % 2 ? roles.moss : roles.reedBase);
      leaves += el('ellipse', { cx: Math.min(w - 4, Math.max(4, px + side * 2.4)), cy: py, rx: 3, ry: 1.4, fill,
        stroke: roles.outline, 'stroke-width': 0.6, transform: `rotate(${n(rot)} ${n(px)} ${n(py)})` });
    }
  }
  const tuftPts: Point[] = [];
  for (let i = 0; i < 9; i++) {
    const a = (i / 9) * Math.PI * 2;
    const lobe = i % 2 ? 0.78 : 1.1; // lumpy, not a flat disc
    tuftPts.push({ x: w / 2 + Math.cos(a) * 13 * lobe * (0.9 + rnd(`t.${i}`) * 0.2), y: 4.3 + Math.sin(a) * 3.4 * lobe * (a > 0 && a < Math.PI ? 1.1 : 0.95) });
  }
  const tuft = el('path', { d: smooth(tuftPts), fill: roles.moss, stroke: roles.outline, 'stroke-width': 0.9 })
    + el('path', { d: `M${n(w / 2 - 9)} 3.4Q${n(w / 2)} 1.8 ${n(w / 2 + 8)} 3.2`, fill: 'none', stroke: roles.reedLight, 'stroke-width': 0.9, opacity: 0.8 });
  return partGroup(c.parts, 'vines-strands', lines) + partGroup(c.parts, 'vines-leaves', leaves) + partGroup(c.parts, 'vines-tuft', tuft);
}

function buildMoss(c: Ctx): string {
  const { rnd, roles, w, h } = c;
  const maxDrop = (h - 4) * c.length;
  // Mat outline: a gently bumpy top edge, then lumpy scallops along the bottom.
  const top: Point[] = [];
  for (let x = 4; x <= w - 4; x += 6) top.push({ x, y: 1.6 + rnd(`top.${x}`) * 1.4 });
  const lumps = 6 + Math.round(2 * c.density);
  const bottom: Point[] = [];
  for (let i = lumps; i >= 0; i--) {
    const x = 4 + (i / lumps) * (w - 8);
    const edge = Math.min(i, lumps - i) / (lumps / 2); // shallower at the ends
    bottom.push({ x, y: 4 + (3 + edge * 4 + rnd(`lump.${i}`) * 3) * (maxDrop / (h - 4)) });
  }
  let d = `M${n(top[0].x)} ${n(top[0].y)}`;
  for (let i = 1; i < top.length; i++) d += `L${n(top[i].x)} ${n(top[i].y)}`;
  d += `Q${n(w - 1.5)} ${n(top[top.length - 1].y + 2)} ${n(bottom[0].x)} ${n(bottom[0].y)}`;
  for (let i = 1; i < bottom.length; i++) {
    const a = bottom[i - 1], b = bottom[i];
    d += `Q${n((a.x + b.x) / 2)} ${n(Math.max(a.y, b.y) + 3 * c.length)} ${n(b.x)} ${n(b.y)}`;
  }
  d += `Q1.5 ${n(top[0].y + 2)} ${n(top[0].x)} ${n(top[0].y)}Z`;
  const mat = el('path', { d, fill: roles.moss, stroke: roles.outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
  // Underside shade and drips.
  let under = '';
  for (let i = 1; i < bottom.length - 1; i++) {
    const b = bottom[i];
    under += el('ellipse', { cx: b.x, cy: b.y - 1.6, rx: 4.2, ry: 1.8, fill: roles.reedShadow, opacity: 0.75 });
  }
  let drips = '';
  const nDrips = 2 + Math.round(3 * c.density);
  for (let i = 0; i < nDrips; i++) {
    const b = bottom[1 + Math.floor(rnd(`drip.${i}`) * (bottom.length - 2))];
    const x = b.x + (rnd(`drip.${i}.x`) - 0.5) * 4;
    const y2 = Math.min(h - 1, b.y + (3 + rnd(`drip.${i}.len`) * 7) * c.length);
    // Soft wavy tendrils with round ends (pointed drips read as teeth along the mat's edge).
    const dd = `M${n(x)} ${n(b.y - 1.5)}Q${n(x + (i % 2 ? 2 : -2))} ${n((b.y + y2) / 2)} ${n(x + (i % 2 ? -0.5 : 0.5))} ${n(y2 - 0.8)}`;
    drips += el('path', { d: dd, fill: 'none', stroke: roles.outline, 'stroke-width': 2.4, 'stroke-linecap': 'round' })
      + el('path', { d: dd, fill: 'none', stroke: roles.reedShadow, 'stroke-width': 1.1, 'stroke-linecap': 'round' });
  }
  // Sunlit top: short pale strokes, never dots.
  let light = '';
  for (let i = 0; i < 6 + Math.round(4 * c.density); i++) {
    const x = 7 + rnd(`l.${i}.x`) * (w - 14), y = 3.4 + rnd(`l.${i}.y`) * 2.2;
    light += `M${n(x)} ${n(y)}q2 -1 4 0`;
  }
  const lights = el('path', { d: light, fill: 'none', stroke: mix(roles.reedLight, roles.salt, 0.2), 'stroke-width': 0.9, 'stroke-linecap': 'round' });
  return partGroup(c.parts, 'moss-drips', drips) + partGroup(c.parts, 'moss-mat', mat + under) + partGroup(c.parts, 'moss-light', lights);
}

const BUILDERS: Readonly<Record<OvergrowthKind, (c: Ctx) => string>> = { ivy_run: buildIvy, hanging_vines: buildVines, moss_clump: buildMoss };

export function buildOvergrowth<K extends OvergrowthKind>(kind: K, recipe: OvergrowthRecipe<K>, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  if (!Object.prototype.hasOwnProperty.call(OVERGROWTH_FRAMES, kind)) throw new Error(`hush overgrowth: unknown kind ${String(kind)}`);
  const gen = `hush.${kind}.v1`;
  checkEnvelope(recipe, gen);
  checkScope(scope);
  const density = num(recipe.params, 'density', 0.4, 1, gen);
  const length = num(recipe.params, 'length', 0.6, 1, gen);
  const f = OVERGROWTH_FRAMES[kind];
  const parts: string[] = [];
  const body = BUILDERS[kind]({ roles, rnd: scrambled(rngFor(recipe)), density, length, w: f.w, h: f.h, parts });
  return { frame: { w: f.w, h: f.h }, body, anchors: { hang: { ...f.hang } }, parts };
}

export const buildIvyRun = (r: OvergrowthRecipe<'ivy_run'>, roles: ResolvedPalette, scope: string) => buildOvergrowth('ivy_run', r, roles, scope);
export const buildHangingVines = (r: OvergrowthRecipe<'hanging_vines'>, roles: ResolvedPalette, scope: string) => buildOvergrowth('hanging_vines', r, roles, scope);
export const buildMossClump = (r: OvergrowthRecipe<'moss_clump'>, roles: ResolvedPalette, scope: string) => buildOvergrowth('moss_clump', r, roles, scope);
